/**
 * Noticing that the MODULES changed, not just the shell.
 *
 * The update banner used to have exactly one trigger: a service worker parked
 * in `waiting`. That only ever happens when `sw.js` itself changes, and `sw.js`
 * ships with the SHELL. A module deploy — which is most deploys — leaves the
 * shell byte-identical, so no worker installs, so the banner cannot fire.
 *
 * The result, measured on boer.metaship.ai 2026-09-15: the module bundle, its
 * `version.json` and `/tenant-apps/resolve` all served the new build, the
 * shell's `version.json` was a day old, and the installed app went on running
 * the previous release with nothing to say so. Every module deploy was silent
 * on a phone.
 *
 * What actually changes on a module deploy is the IMPORT MAP — the hashed
 * bundle URL against each module key. So that is what this watches.
 */
import { fetchTenantApp } from "@/tenant/resolve";
import type { ImportMapEntry } from "@/tenant/types";

/**
 * A stable string for one import map.
 *
 * Sorted, because the server's ordering is not part of the meaning and a
 * reordered map is not an update — raising the banner for one would train
 * people to ignore it. Key AND url, because a module can be added or removed
 * as well as rebuilt.
 */
export function importMapFingerprint(imports: readonly ImportMapEntry[]): string {
  return imports
    .map((entry) => `${entry.key}=${entry.url}`)
    .sort()
    .join("\n");
}

/**
 * A check for "are the modules this page is running still the current ones".
 *
 * Closes over the map the page BOOTED with, which is the honest comparison —
 * what is on screen, not what was current last time we looked.
 *
 * Returns false on any failure. A check that cannot reach the network is not
 * evidence of an update, and an app in a packhouse with no signal must not be
 * told to reload into nothing.
 */
export function makeModuleUpdateCheck(
  booted: readonly ImportMapEntry[],
  host: string,
  apiBase: string,
): () => Promise<boolean> {
  const bootPrint = importMapFingerprint(booted);
  return async () => {
    try {
      const live = await fetchTenantApp(host, apiBase);
      return importMapFingerprint(live.importMap.imports) !== bootPrint;
    } catch {
      return false;
    }
  };
}
